import {ButtonGroup, closeCurrentDialog, DataStatus, DialogFooter} from 'platform/components';
import {getCssSize} from 'platform/foundation';
import styled from 'styled-components';

import {usePreviewEmailNotificationQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {localeByLanguage} from '@dms/shared';

import {useBoolean} from 'shared/web';

interface EmailExamplePreviewProps {
  notificationId: string;
  language: string;
}

const DEFAULT_IFRAME_HEIGHT = 203;

export function EmailExamplePreview(props: EmailExamplePreviewProps) {
  const [isIframeLoaded, loadIframe] = useBoolean(false);

  const {data, isLoading, isError} = usePreviewEmailNotificationQuery({
    id: props.notificationId,
    locale: localeByLanguage[props.language] ?? props.language,
  });

  return (
    <>
      <DataStatus isLoading={isLoading} isError={isError} minHeight={DEFAULT_IFRAME_HEIGHT}>
        {/*
          The endpoint returns a first-party preview document whose actual email is embedded in a
          nested `<iframe srcdoc="...">`. Sanitizing it with DOMPurify strips that nested iframe
          (and its `srcdoc`, whose value legitimately contains markup like `</style>`), leaving an
          empty preview. Isolation is provided by the fully restrictive `sandbox=""` attribute,
          which blocks script execution, so the raw document can be rendered directly.
        */}
        <IFrame
          isIframeLoaded={isIframeLoaded}
          sandbox=""
          srcDoc={data ?? ''}
          loading="eager"
          onLoad={loadIframe}
        />
      </DataStatus>

      <DialogFooter>
        <ButtonGroup
          align="right"
          buttons={[{title: i18n.t('general.actions.close'), onClick: closeCurrentDialog}]}
        />
      </DialogFooter>
    </>
  );
}

const IFrame = styled.iframe<{isIframeLoaded: boolean}>`
  width: 100%;
  height: ${getCssSize(DEFAULT_IFRAME_HEIGHT)};
  visibility: ${({isIframeLoaded}) => (isIframeLoaded ? 'visible' : 'hidden')};
`;
